import React, { useEffect, useState } from 'react'
import { RouteComponentProps, withRouter } from 'react-router-dom'
import styled from 'styled-components'

// @ts-expect-error: no types in this @gnomad/ui version
import { Button, ExternalLink, Modal } from '@gnomad/ui'

import Link from './Link'
import OtherStudies from './OtherStudies'
import Searchbox from './Searchbox'
import { logout, userHasBearerCookie } from './auth'

import gp2LogoSrc from '../gp2/content/gp2logo.svg'

const TitleWrapper = styled.div``

const ToggleMenuButton = styled(Button)``

type TopBarWrapperProps = {
  backgroundColor: string
  textColor: string
}

const TopBarWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 10px 30px;
  margin-bottom: 20px;
  background-color: ${(props: TopBarWrapperProps) => props.backgroundColor};

  @media (max-width: 900px) {
    flex-direction: column;
  }

  ${Link} {
    color: ${(props: TopBarWrapperProps) => props.textColor};
    text-decoration: none;
  }

  ${TitleWrapper} {
    color: ${(props: TopBarWrapperProps) => props.textColor};
    font-size: 1.5em;

    @media (max-width: 900px) {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      align-items: center;
      width: 100%;
      margin-bottom: 0.5em;
    }
  }

  ${ToggleMenuButton} {
    border: 1px solid ${(props: TopBarWrapperProps) => props.textColor};
    background: transparent;
    color: inherit;
    font-size: 1rem;

    @media (min-width: 900px) {
      display: none;
    }
  }
`

type MenuProps = {
  isExpanded: boolean
}

const Menu = styled.ul`
  display: flex;
  flex-direction: row;
  padding: 0;
  margin: 0;
  list-style-type: none;

  ${Link} {
    padding: 0.5em;
    font-size: 16px;
  }

  @media (max-width: 900px) {
    flex-direction: column;
    width: 100%;
    height: ${(props: MenuProps) => (props.isExpanded ? 'auto' : 0)};

    ${Link} {
      display: inline-block;
      width: 100%;
      padding: 1em 0;
    }
  }
`

const Banner = styled.div`
  width: 100%;
  margin-bottom: 20px;
  margin-top: -20px;
  background-color: #f0f0f0;
`

const CenterHorizontal = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
`

interface TopBarOwnProps {
  title: string
  links?: {
    path: string
    label: string
  }[]
  backgroundColor?: string
  textColor?: string
  passwordProtectedDatasets?: string[]
}

type TopBarProps = TopBarOwnProps & RouteComponentProps

const TopBar = ({
  location,
  title,
  links = [],
  backgroundColor = '#000',
  textColor = '#fff',
  passwordProtectedDatasets = [],
}: TopBarProps) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isAuthLoading, setIsAuthLoading] = useState(true)
  const { datasetId } = window.datasetConfig

  useEffect(() => {
    setIsAuthenticated(userHasBearerCookie())
    setIsAuthLoading(false)
  }, [])

  const [isMenuExpanded, setIsMenuExpanded] = useState(false)
  const closeMenu = () => {
    setIsMenuExpanded(false)
  }

  const [showOtherStudiesModal, setShowOtherStudiesModal] = useState(false)

  if (
    isAuthLoading ||
    (passwordProtectedDatasets.includes(datasetId) &&
      (!isAuthenticated || location.pathname === '/login'))
  ) {
    return <></>
  }

  return (
    <>
      <TopBarWrapper backgroundColor={backgroundColor} textColor={textColor}>
        <TitleWrapper>
          <Link to="/" onClick={closeMenu}>
            {datasetId === 'GP2' ? <img src={gp2LogoSrc} alt="GP2 Logo" /> : title}
          </Link>
          <ToggleMenuButton
            onClick={() => {
              setIsMenuExpanded(!isMenuExpanded)
            }}
          >
            ☰
          </ToggleMenuButton>
        </TitleWrapper>

        <Searchbox id="navbar-search" width="320px" />

        <Menu isExpanded={isMenuExpanded}>
          {datasetId !== 'GP2' && (
            <li>
              <Link to="/results" onClick={closeMenu}>
                Results
              </Link>
            </li>
          )}
          {links.map(({ path, label }) => (
            <li key={path}>
              <Link to={path} onClick={closeMenu}>
                {label}
              </Link>
            </li>
          ))}
          {datasetId !== 'GP2' && (
            <li>
              <Link to="/downloads" onClick={closeMenu}>
                Downloads
              </Link>
            </li>
          )}
          <li>
            <Link
              to="/other-studies"
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                setShowOtherStudiesModal(true)
                closeMenu()
                e.preventDefault()
              }}
            >
              Other Studies
            </Link>
          </li>
          {userHasBearerCookie() && (
            <li>
              <Link to="/login" onClick={logout}>
                Logout
              </Link>
            </li>
          )}
        </Menu>

        {showOtherStudiesModal && (
          <Modal
            id="other-studies"
            size="large"
            title="Other Studies"
            onRequestClose={() => {
              setShowOtherStudiesModal(false)
            }}
          >
            <OtherStudies />
          </Modal>
        )}
      </TopBarWrapper>
      {datasetId === 'IBD' && (
        <Banner>
          <CenterHorizontal>
            <p>
              Have feedback? Fill out our{' '}
              <ExternalLink href="https://forms.gle/Zf3BkwwV9dB6TMLA9">Google Form</ExternalLink>
            </p>
          </CenterHorizontal>
        </Banner>
      )}
      {datasetId === 'BipEx' && (
        <Banner>
          <CenterHorizontal>
            <p>
              Have feedback? Fill out our{' '}
              <ExternalLink href="https://forms.gle/VcF8AjgnD9q7eFVw6">
                BipEx feedback Form
              </ExternalLink>
            </p>
          </CenterHorizontal>
        </Banner>
      )}
    </>
  )
}

export default withRouter(TopBar)
